class PupsProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.loaded || this.loading) return;

    this.handleIntersection = (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.unobserve(this);
      this.load();
    };

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: '0px 0px 400px 0px',
      });
      this.observer.observe(this);
    } else {
      this.load();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
  }

  buildUrl() {
    const { url, sectionId, productId } = this.dataset;
    if (!url || !sectionId || !productId) return null;

    const requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionId);
    requestUrl.searchParams.set('product_id', productId);

    const pageParams = new URLSearchParams(window.location.search);
    ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
      if (pageParams.has(key)) requestUrl.searchParams.set(key, pageParams.get(key));
    });

    return requestUrl.toString();
  }

  async load() {
    const requestUrl = this.buildUrl();
    if (!requestUrl || this.loaded || this.loading) return;

    this.loading = true;
    this.controller = new AbortController();

    try {
      const response = await fetch(requestUrl, {
        credentials: 'same-origin',
        signal: this.controller.signal,
      });
      if (!response.ok) return;

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const recommendationElements = [...responseDocument.querySelectorAll('product-recommendations')];
      const matchingElement = recommendationElements.find(
        (element) =>
          element.dataset.sectionId === this.dataset.sectionId &&
          element.dataset.productId === this.dataset.productId,
      );
      const sourceElement = matchingElement || recommendationElements[0];
      if (!sourceElement?.innerHTML.trim()) return;

      this.innerHTML = sourceElement.innerHTML;
      this.loaded = true;
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.loading = false;
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', PupsProductRecommendations);
}

class PupsCartRecommendations extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-cart-recommendations-track]');
    this.previousButton = this.querySelector('[data-cart-recommendations-scroll="previous"]');
    this.nextButton = this.querySelector('[data-cart-recommendations-scroll="next"]');
    if (!this.track || !this.previousButton || !this.nextButton) return;

    this.handlePrevious = () => this.scroll(-1);
    this.handleNext = () => this.scroll(1);
    this.handlePosition = () => this.updateControls();

    this.previousButton.addEventListener('click', this.handlePrevious);
    this.nextButton.addEventListener('click', this.handleNext);
    this.track.addEventListener('scroll', this.handlePosition, { passive: true });
    this.resizeObserver = new ResizeObserver(this.handlePosition);
    this.resizeObserver.observe(this.track);
    this.updateControls();
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.track?.removeEventListener('scroll', this.handlePosition);
    this.resizeObserver?.disconnect();
  }

  scroll(direction) {
    this.track.scrollBy({ left: direction * this.track.clientWidth * 0.82, behavior: 'smooth' });
  }

  updateControls() {
    const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth - 2);
    this.previousButton.disabled = this.track.scrollLeft <= 2;
    this.nextButton.disabled = this.track.scrollLeft >= maximum;
  }
}

if (!customElements.get('pups-cart-recommendations')) {
  customElements.define('pups-cart-recommendations', PupsCartRecommendations);
}
